import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

/** Bottom-anchored and oversized, so the brand mark bleeds off the card edge. */
const WATERMARK_ASIDE = 'bottom-0 right-4 h-48 items-end opacity-90 xl:right-6';

interface HeroSlideLayoutProps {
    /** The welcome slide owns the page heading; every other slide is a section heading. */
    as?: 'h1' | 'h2';
    title: ReactNode;
    description: ReactNode;
    action: ReactNode;
    aside?: ReactNode;
    /**
     * Placement for the decorative aside. Defaults to the bleeding watermark used by the
     * brand mark; solid artwork should center itself instead so it clears the carousel dots.
     */
    asideClassName?: string;
}

export const HeroSlideLayout = ({
    as: Heading = 'h2',
    title,
    description,
    action,
    aside,
    asideClassName = WATERMARK_ASIDE,
}: HeroSlideLayoutProps) => {
    return (
        <div
            className={cn(
                'relative flex flex-col gap-4 px-4 py-5 sm:gap-8 sm:p-8 lg:flex-row lg:items-center lg:justify-between',
                aside && 'lg:pr-24 xl:pr-32',
            )}
        >
            <div className="max-w-2xl space-y-3 sm:space-y-4">
                <div className="space-y-3">
                    <Heading className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-4xl">
                        {title}
                    </Heading>
                    <p className="max-w-xl text-pretty text-sm leading-6 text-muted-foreground sm:text-lg sm:leading-8">
                        {description}
                    </p>

                    {action}
                </div>
            </div>

            {aside && (
                <div
                    aria-hidden="true"
                    className={cn('pointer-events-none absolute hidden lg:flex', asideClassName)}
                >
                    {aside}
                </div>
            )}
        </div>
    );
};
